/**
 * AdminAnnounceTemplates — CRUD page for Announcement Templates
 * Route: /admin/announce-templates
 * Roles: admin, superadmin
 */
import { useState } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { PageHeader, ConfirmDialog, LoadingState } from '../components/admin';
import DataTable from '../components/admin/DataTable';
import BilingualTextField from '../components/admin/BilingualTextField';
import DynamicListInput from '../components/admin/DynamicListInput';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import {
  useAnnounceTemplates,
  useCreateAnnounceTemplate,
  useUpdateAnnounceTemplate,
  useDeleteAnnounceTemplate,
} from '../api/admin/cabinModules';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Plus, Volume2, Edit, Trash2, Info } from 'lucide-react';
import { motion } from 'framer-motion';

const CATEGORIES = [
  { value: 'boarding', labelTr: 'Biniş', labelEn: 'Boarding' },
  { value: 'safety', labelTr: 'Güvenlik', labelEn: 'Safety' },
  { value: 'inflight', labelTr: 'Uçuş İçi', labelEn: 'Inflight' },
  { value: 'landing', labelTr: 'İniş', labelEn: 'Landing' },
  { value: 'emergency', labelTr: 'Acil Durum', labelEn: 'Emergency' },
];

// Konusma dili — STT transcribe default'u (yeni 2026-05-17)
const SPOKEN_LANGUAGES = [
  { value: 'en', labelTr: 'English (EN)', labelEn: 'English (EN)' },
  { value: 'tr', labelTr: 'Türkçe (TR)', labelEn: 'Turkish (TR)' },
];

const EMPTY_SLOT = () => ({
  key: '',
  label_tr: '',
  label_en: '',
  example_tr: '',
  example_en: '',
});

const EMPTY_FORM = {
  id: '',
  category: '',
  title_tr: '',
  title_en: '',
  template_tr: '',
  template_en: '',
  slots: [],
  spoken_language: 'en',
  is_active: true,
};

export default function AdminAnnounceTemplates() {
  const { lang, t } = useLanguage();
  const toast = useToast();

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ category: '', is_active: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const activeFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== '')
  );

  const { data, isLoading } = useAnnounceTemplates(page, activeFilters);
  const createMutation = useCreateAnnounceTemplate();
  const updateMutation = useUpdateAnnounceTemplate();
  const deleteMutation = useDeleteAnnounceTemplate();

  const items = data?.items || data?.templates || [];
  const total = data?.total || 0;
  const totalPages = data?.pages || Math.ceil(total / 20) || 1;

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id || row._id);
    setForm({
      id: row.id || row._id || '',
      category: row.category || '',
      title_tr: row.title_tr || '',
      title_en: row.title_en || '',
      template_tr: row.template_tr || '',
      template_en: row.template_en || '',
      slots: row.slots || [],
      spoken_language: row.spoken_language || 'en',
      is_active: row.is_active !== false,
    });
    setModalOpen(true);
  };

  const fieldChange = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSave = async () => {
    if (!form.title_tr || !form.title_en) {
      toast.error(t('admin.announceTemplates.validationTitleRequired'));
      return;
    }
    if (!form.category) {
      toast.error(t('admin.announceTemplates.validationCategoryRequired'));
      return;
    }
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data: form });
        toast.success(t('admin.announceTemplates.updateSuccess'));
      } else {
        await createMutation.mutateAsync(form);
        toast.success(t('admin.announceTemplates.createSuccess'));
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteMutation.mutateAsync(deleteTarget.id || deleteTarget._id);
      toast.success(t('admin.announceTemplates.deleteSuccess'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
    setConfirmOpen(false);
    setDeleteTarget(null);
  };

  // Extract {{key}} placeholders from template text for preview
  const extractSlotKeys = (text) => {
    const matches = text.match(/\{\{(\w+)\}\}/g) || [];
    return [...new Set(matches.map((m) => m.slice(2, -2)))];
  };

  const detectedKeys = extractSlotKeys(form.template_tr + ' ' + form.template_en);

  const columns = [
    {
      key: 'title_tr',
      label: t('admin.announceTemplates.colTitle'),
      sortable: true,
      render: (_, row) => (
        <span className="font-medium text-gray-800 block max-w-[220px] truncate">{row.title_tr}</span>
      ),
    },
    {
      key: 'category',
      label: t('admin.announceTemplates.colCategory'),
      render: (_, row) => {
        const cat = CATEGORIES.find((c) => c.value === row.category);
        return <Badge variant="secondary" className="text-xs">{(lang === 'tr' ? cat?.labelTr : cat?.labelEn) || row.category}</Badge>;
      },
    },
    {
      key: 'slot_count',
      label: t('admin.announceTemplates.colSlots'),
      render: (_, row) => (
        <Badge variant="outline" className="text-xs">{row.slots?.length ?? row.slot_count ?? 0}</Badge>
      ),
    },
    {
      key: 'is_active',
      label: t('admin.announceTemplates.colActive'),
      render: (_, row) => (
        <Badge className={row.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}>
          {row.is_active ? (lang === 'tr' ? 'Aktif' : 'Active') : (lang === 'tr' ? 'Pasif' : 'Inactive')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => openEdit(row)} className="h-7 px-2">
            <Edit size={14} className="mr-1" />
            {t('admin.btn_edit')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setDeleteTarget(row); setConfirmOpen(true); }} className="h-7 px-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50">
            <Trash2 size={14} className="mr-1" />
            {t('admin.btn_delete')}
          </Button>
        </div>
      ),
    },
  ];

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: t('admin.nav.cabinAcademy'), href: '/admin/cabin-academy' },
          { label: t('admin.nav.announceTemplates') },
        ]}
        title={t('admin.announceTemplates.title')}
        subtitle={t('admin.announceTemplates.subtitle')}
        icon={Volume2}
        actions={
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button onClick={openCreate} className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white">
              <Plus size={16} />
              {t('admin.announceTemplates.createBtn')}
            </Button>
          </motion.div>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={filters.category} onValueChange={(v) => setFilters((f) => ({ ...f, category: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-44 h-9 text-sm">
            <SelectValue placeholder={t('admin.announceTemplates.filterCategory')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin.filter_all')}</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{lang === 'tr' ? c.labelTr : c.labelEn}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.is_active} onValueChange={(v) => setFilters((f) => ({ ...f, is_active: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-32 h-9 text-sm">
            <SelectValue placeholder={t('admin.announceTemplates.filterStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin.filter_all')}</SelectItem>
            <SelectItem value="true">{lang === 'tr' ? 'Aktif' : 'Active'}</SelectItem>
            <SelectItem value="false">{lang === 'tr' ? 'Pasif' : 'Inactive'}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <LoadingState /> : (
        <DataTable
          columns={columns}
          data={items}
          rowKey={(r) => r.id || r._id}
          emptyTitle={t('admin.announceTemplates.emptyTitle')}
          emptyDescription={t('admin.announceTemplates.emptyDesc')}
        />
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t('admin.prev_page')}</Button>
          <span className="text-sm text-gray-600">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>{t('admin.next_page')}</Button>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? t('admin.announceTemplates.editTitle') : t('admin.announceTemplates.createTitle')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* ID + Category */}
            <div className="grid grid-cols-2 gap-3">
              {!editingId && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-600">ID (slug) <span className="text-rose-500">*</span></Label>
                  <Input value={form.id} onChange={(e) => fieldChange('id', e.target.value)} placeholder="boarding_welcome" className="text-sm font-mono" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-600">
                  {t('admin.announceTemplates.fieldCategory')} <span className="text-rose-500">*</span>
                </Label>
                <Select value={form.category} onValueChange={(v) => fieldChange('category', v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder={t('admin.select_placeholder')} /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{lang === 'tr' ? c.labelTr : c.labelEn}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* Konusma Dili — STT transcribe default'u (yeni 2026-05-17) */}
              <div className="space-y-1.5">
                <Label
                  className="text-xs font-semibold text-gray-600"
                  title={lang === 'tr'
                    ? 'Bu anons sablonu icin varsayilan konusma dili. Kullanici override edebilir.'
                    : 'Default spoken language for this announce template. User can override on the practice screen.'}
                >
                  {lang === 'tr' ? 'Konusma Dili' : 'Spoken Language'}
                </Label>
                <Select
                  value={form.spoken_language}
                  onValueChange={(v) => fieldChange('spoken_language', v)}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SPOKEN_LANGUAGES.map((sl) => (
                      <SelectItem key={sl.value} value={sl.value}>
                        {lang === 'tr' ? sl.labelTr : sl.labelEn}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Title */}
            <BilingualTextField
              labelTr={t('admin.announceTemplates.fieldTitleTr')}
              labelEn={t('admin.announceTemplates.fieldTitleEn')}
              nameTr="title_tr"
              nameEn="title_en"
              valueTr={form.title_tr}
              valueEn={form.title_en}
              onChange={fieldChange}
              required
            />

            {/* Template text */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label className="text-xs font-semibold text-gray-600">
                  {t('admin.announceTemplates.fieldTemplateTr')}
                  <span className="ml-1.5 text-[10px] bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded font-bold">TR</span>
                </Label>
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <Info size={12} />
                  {lang === 'tr' ? '{{key}} placeholder kullanın' : 'Use {{key}} placeholders'}
                </div>
              </div>
              <Textarea
                value={form.template_tr}
                onChange={(e) => fieldChange('template_tr', e.target.value)}
                placeholder="Sayın yolcularımız, {{destination}} seferimize hoş geldiniz..."
                rows={4}
                className="text-sm font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">
                {t('admin.announceTemplates.fieldTemplateEn')}
                <span className="ml-1.5 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">EN</span>
              </Label>
              <Textarea
                value={form.template_en}
                onChange={(e) => fieldChange('template_en', e.target.value)}
                placeholder="Ladies and gentlemen, welcome to flight {{destination}}..."
                rows={4}
                className="text-sm font-mono"
              />
            </div>

            {/* Detected keys hint */}
            {detectedKeys.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs text-amber-700 font-semibold mb-1.5">
                  {lang === 'tr' ? 'Tespit edilen slot\'lar:' : 'Detected slots:'}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {detectedKeys.map((k) => (
                    <Badge key={k} className="bg-amber-100 text-amber-800 text-xs font-mono">{`{{${k}}}`}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Slots */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-gray-600">{t('admin.announceTemplates.fieldSlots')}</Label>
              <DynamicListInput
                value={form.slots}
                onChange={(v) => fieldChange('slots', v)}
                emptyItem={EMPTY_SLOT}
                addLabel={lang === 'tr' ? 'Slot Ekle' : 'Add Slot'}
                min={0}
                max={10}
                renderItem={(item, idx, onItemChange) => (
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-600">
                        {lang === 'tr' ? 'Anahtar (key)' : 'Key'} <span className="text-rose-500">*</span>
                      </Label>
                      <Input
                        value={item.key}
                        onChange={(e) => onItemChange({ ...item, key: e.target.value })}
                        placeholder="destination"
                        className="text-sm font-mono w-48"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={item.label_tr} onChange={(e) => onItemChange({ ...item, label_tr: e.target.value })} placeholder="Etiket (TR)" className="text-sm" />
                      <Input value={item.label_en} onChange={(e) => onItemChange({ ...item, label_en: e.target.value })} placeholder="Label (EN)" className="text-sm" />
                      <Input value={item.example_tr} onChange={(e) => onItemChange({ ...item, example_tr: e.target.value })} placeholder="Örnek (TR)" className="text-sm" />
                      <Input value={item.example_en} onChange={(e) => onItemChange({ ...item, example_en: e.target.value })} placeholder="Example (EN)" className="text-sm" />
                    </div>
                  </div>
                )}
              />
            </div>

            {/* Active */}
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => fieldChange('is_active', v)} id="at-is-active" />
              <Label htmlFor="at-is-active" className="text-sm font-medium cursor-pointer">{t('admin.announceTemplates.fieldActive')}</Label>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>{t('admin.btn_cancel')}</Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-cyan-500 hover:bg-cyan-600 text-white">
              {isSaving ? t('admin.btn_loading') : t('admin.btn_save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('admin.announceTemplates.deleteTitle')}
        description={t('admin.announceTemplates.deleteDesc')}
        onConfirm={handleDeleteConfirm}
        loading={deleteMutation.isPending}
      />
    </AdminLayout>
  );
}
